import ApiService from '@core/api/services/ApiService';
import UrlResolver from '@core/url-resolver/UrlResolver';
import debounce from 'lodash/debounce';
import React from 'react';
export var SearchType;
(function (SearchType) {
    SearchType[SearchType["SiteWide"] = 0] = "SiteWide";
    SearchType[SearchType["Products"] = 1] = "Products";
    SearchType[SearchType["TechnicalLibrary"] = 2] = "TechnicalLibrary";
    SearchType[SearchType["MediaArticle"] = 3] = "MediaArticle";
})(SearchType || (SearchType = {}));
export var OrderType;
(function (OrderType) {
    OrderType[OrderType["Relevance"] = 0] = "Relevance";
    OrderType[OrderType["Alphabetically"] = 1] = "Alphabetically";
    OrderType[OrderType["AlphabeticallyDescending"] = 2] = "AlphabeticallyDescending";
    OrderType[OrderType["DateDescending"] = 3] = "DateDescending";
    OrderType[OrderType["Date"] = 4] = "Date";
    OrderType[OrderType["NodeOrder"] = 5] = "NodeOrder";
})(OrderType || (OrderType = {}));
const useSiteSearch = (params) => {
    const [response, setResponse] = React.useState(params.initialData ?? undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    // Identifies the latest request so responses arriving out of order are ignored.
    const latestRequestId = React.useRef(0);
    // The search (term + filters) and page of the results currently shown, used to decide whether to append.
    const shownSearchKey = React.useRef(params.initialData ? getSearchKey(params.request) : null);
    const shownPageIndex = React.useRef(params.initialData ? params.request.pageIndex : 0);
    // Only uses refs and state setters, so it is safe to call from the debounced callback created on first render.
    const fetchResults = async (p) => {
        const requestId = ++latestRequestId.current;
        const searchKey = getSearchKey(p);
        // Append only when loading the next page of the results already shown.
        const append = searchKey === shownSearchKey.current && p.pageIndex === shownPageIndex.current + 1;
        setIsLoading(true);
        try {
            const apiResponse = await ApiService.request({
                baseUrl: UrlResolver.getDomain(),
                method: 'GET',
                controller: 'search',
                slug: 'performsearch',
                params: { ...p },
            });
            if (requestId !== latestRequestId.current) {
                return;
            }
            const result = apiResponse?.result;
            setResponse((previousResponse) => append && result?.results && previousResponse?.results
                ? { ...result, results: [...previousResponse.results, ...result.results] }
                : result);
            shownSearchKey.current = searchKey;
            shownPageIndex.current = p.pageIndex;
        }
        catch {
            // Keep the results currently shown if the request fails.
        }
        finally {
            if (requestId === latestRequestId.current) {
                setIsLoading(false);
            }
        }
    };
    const debouncedFetchResults = React.useCallback(debounce(fetchResults, 275), []);
    React.useEffect(() => {
        if (!params.disableRequest) {
            debouncedFetchResults(params.request);
        }
    }, [
        params.request.searchTerm,
        params.request.selectedFilters?.length,
        params.request.pageIndex,
        params.disableRequest,
    ]);
    return { response, isLoading };
};
/** Identifies a search independently of the page requested. */
function getSearchKey(request) {
    return JSON.stringify([request.searchTerm, request.selectedFilters ?? []]);
}
export default useSiteSearch;
